import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {interval, map, Observable, Subscription} from "rxjs";
import {GameInstanceService} from "../../../services/game-instance.service";
import {IDrawNumberResponse} from "../../../interfaces/IDrawNumberResponse";
import {IEligibleTicketWithDrawDetails} from "../../../interfaces/IEligibleTicketWithDrawDetails";
import {GameService} from "../../../services/game.service";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {GameInstanceStatuses} from "../../../enum/GameInstanceStatusEnum";
import {IActiveGameInstance} from "../../../interfaces/IActiveGameInstance";
import {IDrawNumberRequest} from "../../../interfaces/IDrawNumberRequest";
import {ActiveUserService} from "../../../services/active-user.service";
import {switchMap} from "rxjs/operators";
import {AsyncPipe} from "@angular/common";
import {MatCard} from "@angular/material/card";
import {MatButton} from "@angular/material/button";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {PipesModule} from "../../../pipes/pipes.module";
import {ContainerComponent} from "@coreui/angular";
import {GameInstanceEntryStateTypes} from "../../../enum/GameInstanceEntryStateTypes";

@Component({
  selector: 'app-draw-winner',
  standalone: true,
  templateUrl: './draw-winner-page.component.html',
  imports: [
    AsyncPipe,
    MatCard,
    MatButton,
    MatProgressSpinner,
    PipesModule,
    ContainerComponent
  ],
  styleUrls: ['./draw-winner-page.component.scss']
})
export class DrawWinnerPageComponent implements OnInit, OnDestroy {

  private gameInstanceService: GameInstanceService = inject(GameInstanceService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private gameService: GameService = inject(GameService);

  public endDatePassedButNotReadyMessage: string = 'The game is now over! Just give us a couple of minutes to get ready for the draw!';
  public showEndDatePassedButNotReadyMessage: boolean = false;
  public winnerIsBeingDrawn: boolean = false;
  public gameInstanceHasEndedSub: Subscription | null = null;
  public checkForGameReadyForDrawSub: Subscription | null = null;
  public activeGameInstance$: Observable<IActiveGameInstance> = this.gameInstanceService.selectActiveGameInstance();
  public winnerResponse?: IEligibleTicketWithDrawDetails;
  public winnerPlayed: boolean = false;
  public checkForGameReadyForDraw$: Observable<number> = interval(5000);
  public gameInstanceStates = GameInstanceStatuses;
  public uiState: UIStateEnum = UIStateEnum.ShowData;
  public uiStateForTemplate = UIStateEnum;
  protected readonly GameInstanceEntryStateTypes = GameInstanceEntryStateTypes;

  public activeGameName$: Observable<string> = this.gameService.selectActiveGame$().pipe(
    map(activeGame => activeGame?.Name || '')
  );

  ngOnInit() {
    this.listenForGameEndedEvent();
  }

  private waitForGameToMoveToReadyForDraw() {
    this.checkForGameReadyForDrawSub = this.checkForGameReadyForDraw$.pipe(switchMap(() => {
      return this.gameInstanceService
        .activeGameInstanceByGame(this.gameService.activeGame().Id, this.activeUserService.activeUser().Id)
    }))
      .subscribe({
        next: (activeGameInstance) => {
          if (activeGameInstance.State == GameInstanceStatuses.ReadyForDraw && this.checkForGameReadyForDrawSub) {
            this.checkForGameReadyForDrawSub.unsubscribe();
          }
        }
      })
  }

  private listenForGameEndedEvent() {
    this.gameInstanceHasEndedSub = this.gameInstanceService.gameInstanceHasEnded$.subscribe({
      next: (gameInstanceHasEnded) => {
        if (gameInstanceHasEnded) {
          this.gameInstanceHasEndedSub?.unsubscribe();

          if (!this.checkForGameReadyForDrawSub) {
            this.waitForGameToMoveToReadyForDraw();
          }
        }
      }
    })
  }

  drawWinner() {
    this.winnerIsBeingDrawn = true;
    this.uiState = UIStateEnum.ShowLoading;
    const drawRequest: IDrawNumberRequest = {
      GameInstanceId: this.gameInstanceService.activeGameInstanceId(),
      OverrideValidation: false,
      AdminId: this.activeUserService.activeUser().Id,
      IsRedraw: false,
      GameId: this.gameService.activeGame().Id
    };

    this.gameInstanceService.drawWinner(drawRequest).subscribe({
      next: (responseP: IDrawNumberResponse) => {
        if (responseP.GameInstanceWinnerDetails) {
          this.winnerIsBeingDrawn = false;
          this.winnerResponse = responseP.GameInstanceWinnerDetails;
          this.winnerPlayed = responseP.TicketWasPlayed;
          this.uiState = UIStateEnum.ShowData;
        }
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.winnerIsBeingDrawn = false;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    })
  }

  ngOnDestroy() {
    if (this.checkForGameReadyForDrawSub) {
      this.checkForGameReadyForDrawSub?.unsubscribe()
    }
    if (this.gameInstanceHasEndedSub) {
      this.gameInstanceHasEndedSub?.unsubscribe()
    }
  }
}
